import React from "react";
import styles from "./PlaydateInviteRecipientView.module.css";
import { FamilySummary, PlaydateGuestType, PlaydateInvite, User, UserSummary } from "../../../gql/types";
import { ProfileRowPlacementType } from "../../../components/profile/ProfileDetailRow";
import { formatISODate } from "../../../components/time/TimeFormatter";
import ActionButton from "../../button/ActionButton";
import UnderlineButton from "../../button/UnderlineButton";
import { ParticipantRowWithOthers } from "../../profile/ParticipantRowWithOthers";
import ParticipantSummaryRow from "../ParticipantSummaryRow";
import PlaydateInviteBaseView from "./PlaydateInviteBaseView";
import TimeSelectionSelectionHeader from "../../time/TimeSelectionSelectionHeader";
import TimeSelectionDisplayView from "../../time/TimeSelectionDisplayView";
import { convertScheduleOptionsToDayPartSelections } from "../../../service/playdate/converters/converter";

export type PlaydateInviteRecipientViewProps = {
    user?: User;
    invite: PlaydateInvite;
    onBack: () => void;
    onContinue: () => void; // Confirm handler
    baseZIndex?: number;
};

const PlaydateInviteRecipientView: React.FC<PlaydateInviteRecipientViewProps> = ({
    user,
    invite,
    onBack,
    onContinue,
    baseZIndex,
}) => {
    let representedPerson: UserSummary | FamilySummary | undefined;

    const isFamilyInvite = invite.guestType === PlaydateGuestType.Family;
    const guestList = invite.guests ?? [];

    const userId = user?.id;
    const familyId = user?.family?.id;

    // Step 1: Match user or their family to guest list
    representedPerson = guestList.find((guest) =>
        guest.id === (isFamilyInvite ? familyId : userId)
    );

    // Step 2: Fallback to first guest
    if (!representedPerson && guestList.length > 0) {
        representedPerson = guestList[0];
    }

    // Step 3: Fallback to externalGuests or generic
    if (!representedPerson) {
        if (invite.externalGuests && invite.externalGuests.length > 0) {
            const externalGuest = invite.externalGuests[0];
            representedPerson = {
                firstName: externalGuest.name,
            } as UserSummary;
        } else {
            representedPerson = {
                firstName: "External User",
            } as UserSummary;
        }
    }

    // Safely get representedPerson.id for deduplication
    const representedId = (representedPerson as any)?.id;

    const otherGuests = guestList.filter(
        (guest) => guest.id !== representedId
    );

    const buttonText = invite.viewerResponse && invite.viewerResponse !== null
        ? "Review Your RSVP"
        : "Continue to RSVP"

    return (
        <PlaydateInviteBaseView
            baseZIndex={baseZIndex}
            onBack={onBack}
            title="Playdate Details"
            infoText="Please review the playdate details and let us know if you’d like to join!"
            topIcon="happy"
        >
            <div className={styles.sectionHeader}>Host:</div>
            <ParticipantSummaryRow
                participant={invite.organizer}
                placement={ProfileRowPlacementType.STANDALONE}
                isReadOnly={true}
            />

            <div className={styles.sectionHeader}>Guests:</div>
            <ParticipantRowWithOthers
                mainParticipant={representedPerson}
                otherParticipants={otherGuests}
            />
            <div className={styles.sectionHeader}>Title:</div>
            <div className={styles.title}>
                {invite.title}
            </div>
            <div className={styles.sectionHeader}>Location:</div>
            <div className={styles.title}>
                {invite.location}
            </div>
            <div className={styles.sectionHeader}>
                <TimeSelectionSelectionHeader
                    key={"timeSelectionHeader"}
                    header={"Proposed Dates & Times"}
                />
            </div>
            <TimeSelectionDisplayView
                mode={"daypart"}
                groupByDate={true}
                daypartIconSelected={false}
                selectedDayParts={convertScheduleOptionsToDayPartSelections(invite.scheduleOptions)}
            />
            <div className={styles.sectionHeader}>Details:</div>
            <div className={styles.notes}>
                {invite.details || <em>No message provided</em>}
            </div>
            {invite.rsvpDeadline && (<>
                <div className={styles.sectionHeader}>Respond By:</div>
                <div className={styles.title}>
                    {formatISODate(invite.rsvpDeadline)}
                </div></>)}
            <div className={styles.buttonContainer}>
                <ActionButton
                    onClick={onContinue}
                >
                    {buttonText}
                </ActionButton>
                <UnderlineButton
                    onClick={onBack}
                >
                    I'll make a decision later
                </UnderlineButton>
            </div>
        </PlaydateInviteBaseView>
    );
};

export default PlaydateInviteRecipientView;
